import { Component, OnInit } from "@angular/core";
import { DataProvider }                 from "../../services/data/data";
import { File, Hash, Node, UUID } from "../../../_model";
import { ActivatedRoute, Router } from "@angular/router";
import { DomSanitizer } from "@angular/platform-browser";
import { PHBGlobals } from "../../../_config/config";
import { Iso639_1, IsoCode } from "../../../_model/Language";
import { Tag, TagGroup } from "../../../_model/tags";
import { getTranslationOrDefault } from "../../../_utils/general-utils";
import { isElectron } from "../../../_utils/electron-utils";
import { translate, translateToLang } from "../../../_model/vocab";

interface DynamicTagGroup {
	id: string
	caption: {
		def: string
		[isoCode: string]: string
	}
	tags: DynamicTag[]
	collapsed: boolean
}

interface DynamicTag {
	id: string
	caption: string
	color: string
	group: string
}

interface SearchResult {
	hit: any
	id: UUID
	caption: string
	tags: Tag[]
	size: number
	name: string
	hash: Hash
	isoCode: IsoCode
	modificationTime: number
	mimeType: string
	section: UUID
	file?: Promise<File>
	exists: boolean
}

@Component({
	selector: "page-search",
	templateUrl: "search.html",
	styleUrls: ["search.scss"]
})
export class SearchPage implements OnInit {

	view: string = null;

	loading: boolean = true;
	asyncLoading: boolean = false;

	query: string = "";

	tags: { [key: string]: DynamicTag | Tag } = {};

	private allResults: SearchResult[];
	searchResult: SearchResult[];

	hits: number = 0;

	maxResults = 25;
	loadedNodes = 0;

	expandDetails: boolean = false;

	tagGroups: (TagGroup | DynamicTagGroup)[] = [];

	selectedTags: { [key: number]: Tag[] } = {};

	search_section: DynamicTag[] = [];
	search_language: DynamicTag[] = [];
	search_year: DynamicTag[] = [];

	get isElectron(): boolean {
		return isElectron();
	}

	get allSelectedTags(): (Tag | DynamicTag)[] {
		const tags: (DynamicTag | Tag)[] = [];
		for (let tagGroupID in this.selectedTags) {
			for (let tag of this.selectedTags[tagGroupID]) {
				tags.push(tag);
			}
		}

		for (let tag of [...this.search_section, ...this.search_language, ...this.search_year]) {
			tags.push(tag);
		}

		tags.sort((a, b) => {
			if (getTranslationOrDefault(a.caption, PHBGlobals.language) < getTranslationOrDefault(b.caption, PHBGlobals.language)) {
				return -1;
			}
			if (getTranslationOrDefault(a.caption, PHBGlobals.language) > getTranslationOrDefault(b.caption, PHBGlobals.language)) {
				return 1;
			}
			return 0;
		});

		return tags;
	}

	get showMore(): boolean {
		if (!this.searchResult || !this.allResults || this.loading) {
			return false;
		}
		return this.searchResult.length < this.allResults.length;
	}

	get systemLanguage(): Iso639_1 {
		return PHBGlobals.systemLanguage;
	}

	fileHashes: Hash[] = [];
	error: boolean = false;

	constructor(private data: DataProvider,
	            private router: Router,
	            private route: ActivatedRoute,
	            public sanitizer: DomSanitizer) {
		this.view = localStorage.getItem("_phb_left_view") || "cards";

	}

	async ngOnInit() {
		if (isElectron()) {
			this.fileHashes = await this.data.getFileHashes();
		}

		let q: string = this.route.snapshot.paramMap.get("q");
		if (q) {
			this.query = q;
		}

		if (this.query) {
			await this.data.updateSearchData(this.query);
		}

		const today = new Date();
		const year: number = today.getFullYear();
		const yearTags: DynamicTag[] = [];
		for (let i = year; i > year - 9; i--) {
			yearTags.push({
				id: i + "",
				caption: i + '',
				color: null,
				group: "year"
			});
		}

		let i = year - 8;
		while (i > (new Date()).getFullYear() - 20) {
			i = i - 5;
			yearTags.push({
				id: i + "-" + (i + 4),
				caption: i + "-" + (i + 4),
				color: null,
				group: "year"
			});
		}


		await Promise.all(Object.values(this.data.service.loadingSections));

		const makeTranslation = (translationVar: string): { def: string, [key: string]: string } => {
			const translation = {
				def: translate(translationVar)
			}
			for (let lang of PHBGlobals.plainLanguages) {
				translation[lang.isocode] = translateToLang(translationVar, lang.isocode);
			}
			return translation;
		}

		const dynamicTagGroups: DynamicTagGroup[] = [
			{
				id: "section",
				caption: makeTranslation("search_taggroup_section"),
				tags: PHBGlobals.index.getSections().map(it => {
					return {
						id: it.id,
						caption: it.caption,
						color: it.color,
						group: "section"
					};
				}),
				collapsed: true
			},
			{
				id: "language",
				caption: makeTranslation("search_taggroup_language"),
				tags: PHBGlobals.plainLanguages.map(it => {
					return {
						id: it.isocode,
						caption: it.title,
						color: null,
						group: "language"
					};
				}),
				collapsed: true
			},
			{
				id: "year",
				caption: makeTranslation("search_taggroup_year"),
				tags: yearTags,
				collapsed: true
			},
		]

		const tagGroups = await this.data.getTags(true);
		this.tagGroups = [];
		this.tagGroups.push(...tagGroups);
		this.tags = {};
		for (let tagGroup of this.tagGroups) {
			for (let tag of tagGroup.tags) {
				this.tags[tag.id] = tag;
			}
		}

		this.tagGroups.push(...dynamicTagGroups);

		this.selectedTags = [];

		try {
			const stored: {
				query: string,
				tags: string[][],
				language: Iso639_1[],
				section: UUID[],
				year: number[],
				limit: number,
				start: number
			} = JSON.parse(localStorage.getItem('phb-search-' + PHBGlobals.user.sessionHash));
			if (stored && (stored.query || (stored.section && stored.section.length > 0) || (stored.tags && stored.tags.length > 0) || (stored.year && stored.year.length > 0) || (stored.tags && stored.tags.length > 0))) {

				console.log("query",stored.tags);

				if (stored.query && !this.query) {
					this.query = stored.query;
				}
				if (stored.section) {
					for (let section of stored.section) {
						const tagGroup = this.tagGroups.find(it => it.id === 'section');
						const pos = tagGroup.tags.findIndex(it => it.id === section);
						const tag = tagGroup.tags[pos];
						if (!this.search_section.find(it => it.id === tag.id)) {
							this.search_section.push(tag as DynamicTag);
						}
					}
				}
				if (stored.language) {
					for (let language of stored.language) {
						const tagGroup = this.tagGroups.find(it => it.id === 'language');
						const pos = tagGroup.tags.findIndex(it => it.id === language);
						const tag = tagGroup.tags[pos];
						if (!this.search_language.find(it => it.id === tag.id)) {
							this.search_language.push(tag as DynamicTag);
						}
					}
				}

				if (stored.year) {
					console.log(stored.year)
					for (let year of stored.year) {
						const tagGroup = this.tagGroups.find(it => it.id === 'year');
						const pos = tagGroup.tags.findIndex(it => it.id === year);
						const tag = tagGroup.tags[pos];
						if (!this.search_year.find(it => it.id === tag.id)) {
							this.search_year.push(tag as DynamicTag);
						}
					}
				}

				const resetedTagGroupsInTagArray = [];

				if (stored.tags) {
					for (let tagGroup of stored.tags) {
						for (let tagId of tagGroup) {
							for (let _tagGroup of tagGroups) {
								if(!resetedTagGroupsInTagArray.includes(_tagGroup.id)) {
									this.selectedTags[_tagGroup.id] = [];
									resetedTagGroupsInTagArray.push(_tagGroup.id)
								}

								const tag = _tagGroup.tags.find(a => Object.values(a.caption).includes(tagId));
								if (tag && this.selectedTags[_tagGroup.id].findIndex(it => it.caption === tagId) === -1) {
									this.selectedTags[_tagGroup.id].push(tag);
									break;
								}
							}
						}
					}
				}
			} else {
				this.defaultSelectedTags();
			}
		} catch {
		}

		await this.search();
	}

	defaultSelectedTags() {
		const currentSectionId = this.data.service.currentSection();
		const currentSection = PHBGlobals.index.nodes[currentSectionId];

		this.search_section = [{
			id: currentSection.id,
			caption: currentSection.caption,
			color: currentSection.color,
			group: "section",
		}]

		const currentLanguage = PHBGlobals.language;

		this.search_language = [{
			id: currentLanguage.isocode,
			caption: currentLanguage.title,
			color: null,
			group: "language",
		}]
	}

	async reset() {
		this.selectedTags = [];
		this.search_section = []
		this.search_language = []
		this.search_year = []

		this.defaultSelectedTags();

		await this.search();
	}

	async search() {
		this.loading = true;
		await this.searchNodes();
		this.loading = false;
	}


	private async searchNodes(term: string = null, sectionId: UUID = null): Promise<Node[]> {

		if (term === null) {
			term = this.query;
		}
		const tags: string[][] = [];
		for (let tagGroupId in this.selectedTags) {
			const t: string[] = this.selectedTags[tagGroupId].map(it => getTranslationOrDefault(it.caption, PHBGlobals.language.isocode));
			if (t.length > 0)
				tags.push(t);
		}

		try {
			this.error = false;
			const data = await this.data.advSearch(term,
					tags,
					this.search_language.map(it => it.id as Iso639_1),
					this.search_section.map(it => it.id),
					this.search_year.filter(it => it).map(it => it.id)
				)
			;

			const makeTagFromString = (str: string): Tag | DynamicTag => {
				for (let tagGroup of this.tagGroups) {
					for (let tag of tagGroup.tags) {
						if (Object.values(tag.caption).includes(str))
							return tag;
					}
				}
				return;
			};

			if (data && data.hits.hits) {
				const results: SearchResult[] = [];
				for (let result of data.hits.hits) {
					try {
						const uniqueTags = Array.from(new Map(result._source.tags.map(makeTagFromString).filter(it => !!it).map(item => [item.id, item])).values());
						results.push({
							id: result._id,
							caption: result._source.caption,
							isoCode: result._source.isoCode,
							tags: uniqueTags as Tag[],
							hash: result._source.hash,
							name: result._source.name,
							size: result._source.size,
							modificationTime: result._source.modificationTime * 1000,
							mimeType: result._source.mimeType,
							hit: result,
							section: result._source.section,
							exists: !isElectron() || this.fileHashes.includes(result._source.hash)
						});

					} catch (e) {
						console.error(e);
					}
				}
				this.allResults = results;
				this.load();
				this.hits = results.length;

			}
			return null;
		} catch (e) {
			console.error(e);
			this.searchResult = null;
			this.error = true
			return null;
		}
	}

	load() {
		this.searchResult = [];
		for (let i = 0; i < this.maxResults + this.loadedNodes; i++) {
			if (this.allResults[i]) {
				this.searchResult.push(this.allResults[i]);
			}
		}
	}

	async more() {
		this.loadedNodes = this.loadedNodes + this.maxResults;
		await this.load();
	}

	goBack() {
		this.data.searchData.set(null);
		this.router.navigate(["/node", this.data.currentNode().id]);
	}

	toggleTag(tag: Tag | DynamicTag, group: TagGroup | DynamicTagGroup) {
		let arr = this.selectedTags[group.id]
		if (this['search_' + group.id]) {
			arr = this['search_' + group.id];
		} else {
			if (!this.selectedTags.hasOwnProperty(group.id)) this.selectedTags[group.id] = [];
		}
		const pos = arr.findIndex(it => it.id === tag.id);
		if (pos === -1) {
			arr.push(tag);
		} else {
			arr.splice(pos, 1);
		}
	}

	tagChecked(tag: Tag, group: TagGroup | DynamicTagGroup): boolean {
		let arr = null
		if (this['search_' + group.id]) {
			arr = this['search_' + group.id];
		} else {
			if (!this.selectedTags.hasOwnProperty(group.id)) this.selectedTags[group.id] = [];
			arr = this.selectedTags[group.id];
		}
		return !!arr.filter(it => it).find(it => it.id === tag.id)
	}

	async tagClick(tag: Tag | DynamicTag) {

		const group = this.tagGroups.find(it => it.id === tag.group);
		this.toggleTag(tag, group);

		await this.search();
	}

	async langTagClick(isoCode: IsoCode) {
		const tagGroup = this.tagGroups.find(it => it.id === 'language');
		if (tagGroup) {
			const tags = tagGroup.tags;
			if (tags) {
				const tag = tags.find(it => it.id === isoCode.split('_')[0]);
				await this.tagClick(tag);
			}
		}
	}

	async sectionTagClick(section: UUID) {
	}

	setGroupCollapse(group: TagGroup | DynamicTagGroup | any) {
		if (group.collapsed === undefined) {
			group.collapsed = false;
		} else {
			group.collapsed = !group.collapsed
		}
	}

}
